<template>
  <BaseModal :open="open" @cancel="handleCancel" @update:open="updateOpen($event)" @after-leave="emits('after-leave')" max-w-lg add-padding
             dialog-classes="rounded-t-lg" z-max>
    <template #default>
      <div class="sm:flex sm:items-start">
        <div
          class="mx-auto flex flex-shrink-0 items-center justify-center rounded-full sm:mx-0"
          :class="[computedBgColor,computedIconContainerSize]">
          <Component :is="computedIcon" :class="[computedIconColor,computedIconSize]" aria-hidden="true"/>
        </div>
        <div class="mt-3 w-full text-center sm:mt-0 sm:ml-4 sm:text-left">
          <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">{{ title }}</DialogTitle>
          <div class="mt-2" :class="!(messageLabel || messagePlaceholder) && 'mb-4'">
            <p class="break-words text-sm text-gray-500">{{ description }}</p>
            <p v-if="unsafeHtmlDescription" v-html="unsafeHtmlDescription"
               class="break-words text-sm text-gray-500 mt-4"/>
            <p v-if="additionalDescription" class="break-words text-sm text-gray-500 mt-4">{{
                additionalDescription
              }}</p>
          </div>
          <slot/>
          <FormTextarea
            v-if="messageLabel || messagePlaceholder"
            :model-value="message"
            :label="messageLabel"
            :max-length="messageMaxLength"
            :placeholder="messagePlaceholder" class="mt-6 w-full flex-1"
            :required="requireMessage"
            @update:modelValue="$emit('update:message', $event)"
          />
        </div>
      </div>
    </template>
    <template #bottom>
      <slot name="bottom">
        <BaseModalButtons
          @confirm="handleConfirm" @cancel="handleCancel" :proceed-button-text="proceedButtonText"
          :cancel-button-text="cancelButtonText" :hide-cancel-button="hideCancelButton" :loading="loading"
          :proceed-button-classes="computedButtonStyles" @update:open="updateOpen($event)"
          :proceed-button-id="proceedButtonId" :confirm-href="confirmHref" :close-on-cancel="closeOnCancel"/>
      </slot>
    </template>
  </BaseModal>
</template>

<script setup>
import FormTextarea from "./FormTextarea.vue";
import BaseModal from "./BaseModal.vue";
import BaseModalButtons from "./BaseModalButtons.vue";
import BusinessDraftAiAvatar from "./BusinessDraftAiAvatar.vue";
import {DialogTitle} from "@headlessui/vue";
import {computed} from "vue";
import {InformationCircleIcon, ShieldCheckIcon, ShieldExclamationIcon} from "@heroicons/vue/20/solid/index.js";

const emits = defineEmits(["update:open", "confirm", "cancel", "update:message", "after-leave"]);
const props = defineProps({
  open: Boolean,
  title: String,
  description: String,
  unsafeHtmlDescription: String,
  additionalDescription: String,
  message: String,
  messageLabel: String,
  messagePlaceholder: String,
  requireMessage: Boolean,
  hideCancelButton: Boolean,
  preventOpenChanges: Boolean,
  proceedButtonId: String,
  confirmHref: String,
  messageMaxLength: {
    type: Number,
    default: 255
  },
  proceedButtonText: {
    type: String,
    default: "Proceed"
  },
  cancelButtonText: {
    type: String,
    default: "Cancel"
  },
  level: {
    type: String,
    default: "danger",
    validator: (value) => ["danger", "warning", "success", "info", "ai"].includes(value)
  },
  subLevel: {
    type: String,
    default: "",
    validator: (value) => ["", "danger", "warning", "success", "info"].includes(value)
  },
  incompleteMessageWarning: {
    type: String,
    default: "Please complete the message field."
  },
  messageValidationCallback: {
    type: Function,
    default: () => true
  },
  closeOnConfirm: {
    type: Boolean,
    default: true
  },
  closeOnCancel: {
    type: Boolean,
    default: true
  },
  loading: Boolean,
});

const levelConfig = {
  danger: {
    icon: ShieldExclamationIcon,
    bgColor: "bg-red-100",
    iconColor: "text-red-600",
    buttonStyles: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
    iconSize: "h-6 w-6",
    iconContainerSize: "h-12 w-12 sm:h-10 sm:w-10"
  },
  warning: {
    icon: ShieldExclamationIcon,
    bgColor: "bg-yellow-100",
    iconColor: "text-yellow-600",
    buttonStyles: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
    iconSize: "h-6 w-6",
    iconContainerSize: "h-12 w-12 sm:h-10 sm:w-10"
  },
  success: {
    icon: ShieldCheckIcon,
    bgColor: "bg-green-100",
    iconColor: "text-green-600",
    buttonStyles: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
    iconSize: "h-6 w-6",
    iconContainerSize: "h-12 w-12 sm:h-10 sm:w-10"
  },
  info: {
    icon: InformationCircleIcon,
    bgColor: "bg-blue-100",
    iconColor: "text-blue-600",
    buttonStyles: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
    iconSize: "h-6 w-6",
    iconContainerSize: "h-12 w-12 sm:h-10 sm:w-10"
  },
  ai: {
    icon: BusinessDraftAiAvatar,
    bgColor: "",
    iconColor: "",
    buttonStyles: "btn-hire focus:ring-bdOrange-200",
    iconSize: "h-12 w-12",
    iconContainerSize: "h-12 w-12"
  }
};

const updateOpen = (open) => {
  if (props.preventOpenChanges) return;
  emits("update:open", open);
};

const computedBgColor = computed(() => levelConfig[props.level].bgColor);
const computedIconColor = computed(() => levelConfig[props.level].iconColor);
const computedIcon = computed(() => levelConfig[props.level].icon);
const computedButtonStyles = computed(() => props.subLevel ? levelConfig[props.subLevel].buttonStyles : levelConfig[props.level].buttonStyles);
const computedIconSize = computed(() => levelConfig[props.level].iconSize);
const computedIconContainerSize = computed(() => levelConfig[props.level].iconContainerSize);


const handleConfirm = () => {
  if (props.requireMessage && !props.message) {
    toast.warning(props.incompleteMessageWarning);
    return;
  }

  if (props.messageValidationCallback && !props.messageValidationCallback(props.message)) {
    return;
  }

  emits("confirm");
  if (props.closeOnConfirm) updateOpen(false);
};

const handleCancel = (wasClickOutside) => emits("cancel", wasClickOutside);
</script>
